import type {WorkspacePermission, WorkspaceRole} from '@/lib/types/roles';
import useLoginStore from '@/store/LoginStore'
import {   hasPermission } from '@/lib/types/roles'

export function useCurrentUserRole(): string {
  return useLoginStore((s) => s.userRole)
}

/**
 * Resolve the caller's grant for a given workspace permission.
 *
 * Backend authorization is permission-based — custom roles can hold any
 * permission set, not just the three built-in roles. The hook mirrors that:
 *
 *   1. When `userPermissionsLoaded` is true, the live `userPermissions`
 *      array is authoritative. An empty live array means "no grants"; a
 *      custom role with the permission is recognized correctly; a built-in
 *      admin role's full permission set comes through verbatim.
 *
 *   2. When the live array hasn't been populated yet (`userPermissionsLoaded`
 *      = false), fall back to the static ROLE_PERMISSIONS map keyed by
 *      `userRole`. Without the loaded signal we couldn't distinguish
 *      "loading" from "loaded with empty grants" — and would over-grant via
 *      the static fallback every render a custom role's array was empty.
 */
export function useHasPermission(permission: WorkspacePermission): boolean {
  const role = useLoginStore((s) => s.userRole)
  const userPermissions = useLoginStore((s) => s.userPermissions)
  const userPermissionsLoaded = useLoginStore((s) => s.userPermissionsLoaded)

  if (userPermissionsLoaded) {
    return userPermissions.includes(permission)
  }
  return hasPermission(role as WorkspaceRole, permission)
}

export function useIsAdmin(): boolean {
  return useCurrentUserRole() === 'admin'
}
